import {inject, Injectable} from '@angular/core';
import {StringService} from '@core/services/string/string.service';
import {LibrarySelectors} from '@store/library';
import {Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import {BookInterface} from '@shared/interfaces/book.interface';
import {TableOfContentsInterface} from '@shared/interfaces/table-of-contents.interface';
import {map, mergeMap} from 'rxjs/operators';
import {ApiV2EndpointsEnum} from '@core/enums/api-v2-endpoints.enum';
import {LinkHandlerResourceInterface} from '@shared/modules/content/modules/link/link.contract';

@Injectable({
    providedIn: 'root',
})
export class DocumentResourceLinkService {
    #store = inject(Store);

    #stringService = inject(StringService);

    book!: BookInterface;

    tableOfContents?: TableOfContentsInterface;

    /**
     * When DOMStringMap contains link, JSON parses it. Because we have no idea if we have a dps or document link, well
     * use a generic that must extend ApiResourceLinkDataLinkInterface, code that uses this must validate if it's the correct type.
     */
    getLinkDataFromElementDataset<T extends LinkHandlerResourceInterface>(domStringMap: DOMStringMap): T | undefined {
        return undefined !== domStringMap.link ? JSON.parse(domStringMap.link) : undefined;
    }

    getResourceURLFromLinkData(id: string, ext: string): Observable<string> {
        return this.#store.select(LibrarySelectors.selectBook)
            .pipe(mergeMap(book => {
                if (undefined === book) {
                    throw new Error('Book is undefined');
                }

                return this.#getDocumentRoute(ext, book.uuid, id);
            }));
    }

    #getDocumentRoute(ext: string, bookUuid: string, id: string): Observable<string> {
        switch (ext) {
            case 'other':
                return new Observable(subscriber => subscriber.next(this.#stringService.getMappedString(ApiV2EndpointsEnum.DocumentSourcesBook, new Map([
                    ['bookId', bookUuid],
                    ['dpsId', id],
                ]))));
            default:
                return this.#getDocumentRouteWithDpsid(bookUuid, id);
        }
    }

    /**
     * This method generates angular route for cross-links
     */
    #getDocumentRouteWithDpsid(bookUuid: string, id: string): Observable<string> {
        return this.#store.select(LibrarySelectors.selectChapter)
            .pipe(map(chapter => {
                if (undefined === chapter) {
                    throw new Error('Chapter is undefined');
                }

                return this.#stringService.getMappedString('books/:bookId/chapters/:chapterUuid/documents/:dpsId', new Map([
                    ['bookId', bookUuid],
                    ['chapterUuid', chapter.uuid],
                    ['dpsId', id],
                ]));
            }));
    }
}
